import { getLocaleString } from '@assets/i18n/config';
import {
  formatMoneyValue,
  isCurrencySymbolAfter,
} from '@utils/moneyFormatting';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartOptions,
  Filler,
  LinearScale,
  type Plugin,
  PointElement,
  Tooltip,
} from 'chart.js';
import { useTheme } from 'hephaestus/web';
import { Bar } from 'react-chartjs-2';
import { useBarChartTokens } from './barChart.tokens';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Filler,
  Tooltip,
);

type IAxis = {
  displayGrid?: boolean;
  borderDisplay?: boolean;
  position?: 'right' | 'left' | 'top' | 'bottom';
  dash?: number[];
  color?: string;
  font?: {
    family?: string;
    size?: number;
    weight?: string;
  };
  min?: number;
  max?: number;
};

type IBarChart = {
  labels: string[];
  datasets: any[];
  format?: 'CENTS' | 'FLOAT';
  xAxis?: IAxis;
  yAxis?: IAxis;
  overrideOptions?: ChartOptions<'bar'>;
  testId?: string;
  plugins?: Plugin<'bar'>[];
  noYAxis?: boolean;
};

const abbreviateNumber = (
  value: number,
  format: 'CENTS' | 'FLOAT' = 'FLOAT',
  currencySymbol = '$',
) => {
  let newValue = value.toString();
  if (Math.abs(value) >= 1000) {
    const suffixes = ['', 'K', 'M', 'B', 'T'];
    const suffixNum = Math.floor((`${value}`.length - 1) / 3);
    let shortValue = 0;
    for (let precision = 2; precision >= 1; precision--) {
      shortValue = parseFloat(
        (suffixNum !== 0 ? value / 1000 ** suffixNum : value).toPrecision(
          precision,
        ),
      );
      const dotLessShortValue = `${shortValue}`.replace(/[^a-zA-Z 0-9]+/g, '');
      if (dotLessShortValue.length <= 3) {
        break;
      }
    }
    let shortValueString = shortValue.toString();
    if (shortValue % 1 !== 0) shortValueString = shortValue.toFixed(1);
    newValue = shortValueString + suffixes[suffixNum];
  }
  if (format === 'CENTS') {
    const locale = getLocaleString();
    const symbolAfter = isCurrencySymbolAfter(locale);
    newValue = symbolAfter
      ? `${newValue} ${currencySymbol}`
      : `${currencySymbol}${newValue}`;
  }
  return newValue;
};

export const BarChart = ({
  labels,
  datasets,
  format = 'CENTS',
  xAxis,
  yAxis,
  noYAxis = false,
  testId = 'bar-chart',
  plugins,
  overrideOptions = {},
}: IBarChart) => {
  const theme = useTheme();
  const tokens = useBarChartTokens(theme);

  const INDEX = 'index';
  const NEAREST = 'nearest';
  const options = {
    animation: {
      duration: 0,
    },
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as typeof INDEX,
    },
    plugins: {
      tooltip: {
        position: 'nearest' as typeof NEAREST,
        callbacks: {
          label: (data: any) =>
            `${format === 'CENTS' ? formatMoneyValue(data.raw) : data.raw}`,
        },
      },
    },
    hover: {
      intersect: false,
      mode: 'point' as const,
    },
    tooltips: {
      mode: 'index',
    },
    scales: {
      x: {
        display: tokens.showAxis || true,
        grid: {
          display: !!xAxis?.displayGrid,
        },
        border: {
          display: xAxis?.borderDisplay ?? true,
        },
        ...(xAxis?.min && { min: xAxis.min }),
        ...(xAxis?.max && { max: xAxis.max }),
        position: xAxis?.position ?? 'bottom',
        ticks: {
          enabled: tokens.showAxis || true,
          maxRotation: 0,
          color: xAxis?.color,
          font: xAxis?.font,
        },
      },
      y: {
        display: tokens.showAxis || !noYAxis,
        border: {
          dash: yAxis?.dash,
          display: yAxis?.borderDisplay ?? true,
        },
        ...(yAxis?.min && { min: yAxis.min }),
        ...(yAxis?.max && { max: yAxis.max }),
        grid: {
          display: !!yAxis?.displayGrid,
        },
        position: yAxis?.position ?? 'left',
        ticks: {
          callback: (value: any) =>
            format === 'CENTS'
              ? `${abbreviateNumber(Math.floor(value / 100), format)}`
              : value,
          maxTicksLimit: 4,
          color: yAxis?.color,
          font: yAxis?.font,
        },
      },
    },
    layout: {
      padding: {
        right: tokens.showAxis ? 16 : 0,
        bottom: tokens.showAxis ? 16 : 0,
      },
    },
  };

  const fullData = {
    labels,
    datasets: (datasets || []).map((x: any) => ({
      fill: true,
      ...x,
      label: x.label,
      data: x.data,
      borderColor: x.borderColor || tokens.color,
      backgroundColor: x.backgroundColor || tokens.color,
      borderRadius: tokens.radius,
    })),
  };

  return (
    <Bar
      options={{ ...options, ...overrideOptions } as any}
      data={fullData}
      data-testid={testId}
      plugins={plugins}
    />
  );
};
